import { Link } from "react-router-dom";
import { ArrowRight, FileCheck2, Globe2, ShieldCheck } from "lucide-react";
import { IMAGES, IMMIGRATION_SERVICES } from "@/data/site";
import { Button } from "@/components/ui/button";
import { CTABand } from "@/components/CTABand";
import { PageHero } from "@/components/PageHero";
import { Reveal, ScaleIn } from "@/components/Motion";
import { ServiceCard } from "@/components/ServiceCard";

const journey = [
  ["USCIS", "Applications, receipts, biometrics, interview preparation, and follow-up notices."],
  ["NVC", "Case creation, civil documents, affidavit records, fees, and status updates."],
  ["Embassy", "Consular appointment organization and interview-ready document checklists."],
];

export default function ImmigrationServices() {
  return (
    <>
      <PageHero
        eyebrow="Immigration Services"
        titleLines={["Professional Immigration", "Document Preparation", "You Can Trust"]}
        description="Family petitions, adjustment of status, naturalization, USCIS applications, NVC support, consular processing, translations, FOIA requests, and more — organized with care and confidentiality."
        image={IMAGES.passport}
        imageAlt="Passport and immigration documents"
      >
        <Reveal delay={0.65} className="mt-10 flex flex-col gap-4 sm:flex-row">
          <Button asChild size="lg" className="rounded-full bg-brand-copper px-8 text-brand-navy hover:bg-brand-copper/90">
            <Link to="/contact" data-testid="immigration-hero-consultation-cta">Start Your Immigration Process <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-white/25 bg-transparent px-8 text-white hover:bg-white hover:text-brand-navy">
            <Link to="/contact" data-testid="immigration-hero-upload-cta">Upload Your Documents</Link>
          </Button>
        </Reveal>
      </PageHero>
      <section className="section-padding" data-testid="immigration-catalog-section">
        <div className="section-shell">
          <Reveal className="mb-14 max-w-3xl">
            <p className="overline text-brand-copper">Comprehensive Support</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl lg:text-5xl">One office for the documents that move your case forward.</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {IMMIGRATION_SERVICES.map((service, index) => (
              <ServiceCard key={service.slug} service={service} index={index} compact />
            ))}
          </div>
        </div>
      </section>
      <section className="section-padding bg-brand-navy text-white" data-testid="immigration-journey-section">
        <div className="section-shell grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal>
            <p className="overline text-brand-copper">Every Stage</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl lg:text-5xl">From USCIS to NVC and U.S. Embassies.</h2>
            <p className="mt-6 text-base leading-8 text-white/68">
              Immigration matters often move through several agencies. Egaal helps clients stay organized as requirements, notices, and document requests change.
            </p>
          </Reveal>
          <div className="space-y-5">
            {journey.map(([title, description], index) => (
              <Reveal key={title} delay={index * 0.08} className="flex gap-6 rounded-lg border border-white/10 bg-white/[0.04] p-7">
                <span className="font-serif text-5xl font-black text-brand-copper/45">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-serif text-2xl font-bold">{title}</h3>
                  <p className="mt-3 text-sm leading-7 text-white/64">{description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="section-padding" data-testid="immigration-care-section">
        <div className="section-shell grid items-center gap-12 lg:grid-cols-3">
          {[
            { icon: FileCheck2, title: "Preparation", text: "Forms, supporting evidence, translations, copies, and filing-ready organization." },
            { icon: Globe2, title: "Guidance", text: "Plain-language explanations of administrative steps, agency correspondence, and next actions." },
            { icon: ShieldCheck, title: "Boundaries", text: "We do not provide legal advice or representation and can identify when attorney review may be appropriate." },
          ].map((item, index) => {
            const Icon = item.icon;
            return (
              <ScaleIn key={item.title} delay={index * 0.08} className="rounded-lg bg-white p-8 shadow-sm">
                <Icon className="h-8 w-8 text-brand-copper" />
                <h3 className="mt-7 font-serif text-2xl font-bold">{item.title}</h3>
                <p className="mt-4 text-sm leading-7 text-brand-navy/65">{item.text}</p>
              </ScaleIn>
            );
          })}
        </div>
      </section>
      <CTABand id="immigration-services" title="Bring us your notices, questions, and goals. We will help you organize the next step." />
    </>
  );
}
